import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { Button } from '@components/ui/button';
import { MoreHorizontal } from 'lucide-react';
import { shortDay, shortFullDate } from '@utils/i18n';
import { cn } from '@utils/cn';
import { jobDayStatusStyle } from '../constants';
import { JobDaysNavigationProps } from '../types';

export const JobDaysMenu = ({ days, activeKey, onSelect }: JobDaysNavigationProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'pages.job.days' });
  const [open, setOpen] = useState(false);

  const scrollToActive = (list: HTMLDivElement | null) => {
    const active = list?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!list || !active) return;
    list.scrollTop = active.offsetTop - (list.clientHeight - active.clientHeight) / 2;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          disabled={false}
          className="h-8 w-8 shrink-0 p-0 text-neutral-500 hover:text-neutral-800"
          aria-label={t('allDays', { count: days.length })}>
          <MoreHorizontal className="h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="z-[60] w-fit min-w-60 rounded-md border-none p-0 text-sm shadow-stage-menu">
        <div className="border-b border-neutral-200 px-4 py-2 text-xs font-bold text-neutral-500">
          {t('allDays', { count: days.length })}
        </div>
        <div ref={scrollToActive} className="scrollbar-thin relative max-h-96 overflow-y-auto">
          {days.map((day) => (
            <button
              key={day.key}
              type="button"
              aria-current={day.key === activeKey}
              onClick={() => {
                onSelect(day.key);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center justify-between gap-4 border-b border-neutral-200 px-4 py-2 text-left text-xs text-neutral-700 last:border-b-0 hover:bg-neutral-50',
                day.key === activeKey && 'bg-primary-50 font-bold',
              )}>
              <span>{[shortDay(day.day), shortFullDate(day.day)].join(', ')}</span>
              <span
                className={cn(
                  'rounded border px-2 py-0.5 text-[10px] font-normal',
                  jobDayStatusStyle[day.status],
                )}>
                {t(`status.${day.status}`)}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
